import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BedDouble, Ticket, Luggage, Quote, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Seo, faqLd } from "../lib/seo";
import { EmailCapture } from "../components/EmailCapture";

function PageShell({ overline, title, intro, children, testid }) {
  return (
    <main className="container-tpb py-14 sm:py-20" data-testid={testid}>
      <p className="overline">{overline}</p>
      <h1 className="mt-3 max-w-3xl text-4xl font-medium tracking-tight sm:text-5xl">{title}</h1>
      {intro && <p className="mt-5 max-w-2xl text-lg leading-relaxed text-ink-muted">{intro}</p>}
      <div className="mt-12">{children}</div>
    </main>
  );
}

export function HowItWorks() {
  const steps = [
    { icon: CalendarDays, title: "Choose your dates", copy: "Pick a start date and how many nights — or stay flexible to see the widest availability." },
    { icon: BedDouble, title: "Choose your hotel", copy: "You'll see the hand-picked hotels available for your dates and choose the one that fits." },
    { icon: Ticket, title: "Pick your experiences", copy: "Add any three from a hand-picked local collection — they're included in the price." },
    { icon: Luggage, title: "Pack your bags", copy: "Hotel, breakfast and experiences sorted in one simple price for two, confirmed in one booking." },
  ];
  return (
    <PageShell
      overline="How it works"
      title="Three steps between you and away"
      intro="A Perfect Break is deliberately simple: we've done the finding, comparing and combining so you don't have to."
      testid="how-it-works-page"
    >
      <Seo
        title="How It Works | Hotel + 3 Experiences, One Price for Two | The Perfect Break"
        description="Choose your break, pick your three included experiences, pack your bags. How The Perfect Break makes UK short breaks for adults effortless."
        path="/how-it-works"
      />
      <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
        {steps.map((s, i) => (
          <div key={s.title} className="rounded-lg border border-sandline bg-white p-8" data-testid={`how-step-${i}`}>
            <span className="font-serif text-6xl font-medium text-sandline">{i + 1}</span>
            <s.icon className="mt-4 h-6 w-6 text-primary" />
            <h2 className="mt-3 font-sans text-xl font-semibold text-ink">{s.title}</h2>
            <p className="mt-2 leading-relaxed text-ink-muted">{s.copy}</p>
          </div>
        ))}
      </div>
      <div className="mt-14 rounded-lg bg-primary p-10 text-center text-sand">
        <h2 className="font-serif text-3xl font-medium">From £169 for two — everything included</h2>
        <Link to="/breaks" className="btn-accent mt-6" data-testid="how-it-works-cta">Find your perfect break <ArrowRight className="h-4 w-4" /></Link>
      </div>
    </PageShell>
  );
}

export function About() {
  return (
    <PageShell
      overline="About us"
      title="Short breaks for adults, done properly"
      testid="about-page"
    >
      <Seo
        title="About The Perfect Break | UK Short Breaks for Adults"
        description="The Perfect Break creates great-value UK short breaks for adults: hand-picked hotels plus three included experiences, from £169 for two. Part of the Stay Play Explore family."
        path="/about"
      />
      <div className="max-w-3xl space-y-6 leading-relaxed text-ink">
        <p>
          The Perfect Break exists for a simple reason: booking a genuinely good short break for two adults shouldn't require an evening of research, a spreadsheet and a leap of faith.
        </p>
        <p>
          We hand-pick comfortable hotels and wrap them together with breakfast and three experiences you choose yourself — heritage sites, food and drink, rivers, gardens and galleries. One break, one price for two people, from £169. We're starting in the Heart of England, a region we know inside out, with more UK destinations on the way.
        </p>
        <div className="rounded-lg border border-sandline bg-white p-6" data-testid="about-relationship-note">
          <h2 className="font-sans text-lg font-semibold text-ink">Our booking partner</h2>
          <p className="mt-2 text-sm leading-relaxed text-ink-muted">
            The Perfect Break is part of the Stay Play Explore family. When you're ready to book, secure booking is completed through our established Stay Play Explore booking system — the same team, the same secure checkout, with payment by major cards, PayPal (including Pay in 3), Google Pay and Apple Pay.
          </p>
        </div>
        <p>
          We're grown-up but not stuffy, good value but never cheap-feeling, and we believe the best part of any break is the bit you'd never have booked on your own.
        </p>
      </div>
    </PageShell>
  );
}

export function Faqs() {
  const [faqs, setFaqs] = useState([]);
  useEffect(() => {
    api.get("/faqs").then((r) => setFaqs(r.data)).catch(() => {});
  }, []);
  return (
    <PageShell
      overline="FAQs"
      title="Questions, answered"
      intro="Everything people usually ask before their first Perfect Break."
      testid="faqs-page"
    >
      <Seo
        title="FAQs | The Perfect Break — UK Short Breaks for Adults"
        description="What's included, how pricing works for two, how booking works, payment options and more — frequently asked questions about The Perfect Break."
        path="/faqs"
        jsonLd={faqs.length ? faqLd(faqs) : undefined}
      />
      <div className="grid gap-6 md:grid-cols-2">
        {faqs.map((f, i) => (
          <div key={f.id || i} className="rounded-lg border border-sandline bg-white p-6" data-testid={`faq-item-${i}`}>
            <h2 className="font-sans text-base font-semibold text-ink">{f.q}</h2>
            <p className="mt-2 text-sm leading-relaxed text-ink-muted">{f.a}</p>
          </div>
        ))}
      </div>
    </PageShell>
  );
}

export function Reviews() {
  const [testimonials, setTestimonials] = useState(null);
  useEffect(() => {
    api.get("/testimonials").then((r) => setTestimonials(r.data)).catch(() => setTestimonials([]));
  }, []);
  return (
    <PageShell
      overline="Reviews"
      title="What our breakers say"
      testid="reviews-page"
    >
      <Seo
        title="Customer Reviews | The Perfect Break"
        description="Genuine reviews from guests on The Perfect Break's UK short breaks for adults."
        path="/reviews"
      />
      {testimonials && testimonials.length === 0 && (
        <div className="rounded-lg border border-sandline bg-white p-10 text-center" data-testid="reviews-empty">
          <Quote className="mx-auto h-8 w-8 text-sandline" />
          <h2 className="mt-4 font-serif text-2xl font-semibold text-primary">We're collecting our first verified reviews</h2>
          <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-ink-muted">
            We only publish genuine customer feedback — never invented quotes. As our first breakers return, their reviews will appear here.
          </p>
        </div>
      )}
      {testimonials && testimonials.length > 0 && (
        <div className="grid gap-6 md:grid-cols-3" data-testid="reviews-grid">
          {testimonials.map((t, i) => (
            <figure key={i} className="rounded-lg border border-sandline bg-white p-6" data-testid={`review-${i}`}>
              <Quote className="h-5 w-5 text-accent" />
              <blockquote className="mt-3 leading-relaxed text-ink">{t.quote}</blockquote>
              <figcaption className="mt-4 text-sm font-semibold text-ink-muted">{t.author} · {t.break_name}</figcaption>
            </figure>
          ))}
        </div>
      )}
    </PageShell>
  );
}

export function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/contact", form);
      toast.success(data.message);
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong — please try again.");
    } finally {
      setBusy(false);
    }
  };
  const field = "w-full rounded-lg border border-sandline bg-white px-4 py-3 text-sm text-ink placeholder:text-ink-muted";
  return (
    <PageShell
      overline="Contact"
      title="Talk to a human"
      intro="Questions about a break, accessibility needs, or anything else — we're happy to help."
      testid="contact-page"
    >
      <Seo
        title="Contact Us | The Perfect Break"
        description="Get in touch with The Perfect Break team about UK short breaks for adults."
        path="/contact"
      />
      <form onSubmit={submit} className="max-w-xl space-y-4" data-testid="contact-form">
        <div>
          <label htmlFor="contact-name" className="mb-1.5 block text-sm font-semibold text-ink">Name</label>
          <input id="contact-name" required className={field} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name-input" />
        </div>
        <div>
          <label htmlFor="contact-email" className="mb-1.5 block text-sm font-semibold text-ink">Email</label>
          <input id="contact-email" type="email" required className={field} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email-input" />
        </div>
        <div>
          <label htmlFor="contact-message" className="mb-1.5 block text-sm font-semibold text-ink">Message</label>
          <textarea id="contact-message" required rows={5} className={field} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-message-input" />
        </div>
        <button type="submit" disabled={busy} className="btn-primary disabled:opacity-60" data-testid="contact-submit-button">
          {busy ? "Sending…" : "Send message"}
        </button>
      </form>
    </PageShell>
  );
}

const LEGAL = {
  privacy: {
    title: "Privacy notice",
    body: [
      ["What we collect", "If you join our mailing list we store your email address, your consent and when you gave it. If you contact us we store your name, email and message. We use privacy-respecting analytics to understand how the site is used; analytics cookies only run with your consent."],
      ["What we never do", "We never sell your data, and we never see or store your payment details — secure booking and payment are completed through our established Stay Play Explore booking system."],
      ["Your rights", "Under UK GDPR you can access, correct or delete your data, or withdraw consent, at any time. Email us via the contact page and we'll action it promptly."],
    ],
  },
  cookies: {
    title: "Cookie policy",
    body: [
      ["Essential cookies", "A small number of cookies keep the site working (for example, remembering your cookie choice). These don't require consent."],
      ["Analytics & marketing cookies", "These only run if you accept them. They help us understand which breaks people love and whether our marketing works. You can change your choice at any time."],
    ],
  },
  terms: {
    title: "Website terms",
    body: [
      ["About this site", "The Perfect Break is a marketing and discovery website for UK short breaks for adults. Guide prices shown were correct when last verified — the live, binding price and availability are always shown on the booking system before you pay."],
      ["Accuracy", "We work hard to keep descriptions, inclusions and guide prices accurate, and we review them regularly. If something has changed, the booking system's information takes precedence."],
    ],
  },
  "booking-terms": {
    title: "Booking terms",
    body: [
      ["How booking works", "When you choose to book, you're taken to the specific package on our established Stay Play Explore booking system. Your contract, payment, confirmation, and any amendment or cancellation terms are provided there and in your confirmation email."],
      ["Payment", "Major credit and debit cards, PayPal (including Pay in 3), Google Pay and Apple Pay are accepted on the booking system."],
    ],
  },
  accessibility: {
    title: "Accessibility",
    body: [
      ["Our commitment", "We aim for WCAG 2.2 AA: semantic structure, keyboard navigation, visible focus states, sufficient contrast, alt text and labelled forms. If anything on this site is hard to use, tell us and we'll fix it."],
    ],
  },
};

export function LegalPage({ page }) {
  const content = LEGAL[page];
  return (
    <PageShell overline="Legal" title={content.title} testid={`legal-${page}`}>
      <Seo title={`${content.title} | The Perfect Break`} description={`${content.title} for The Perfect Break — UK short breaks for adults.`} path={`/${page}`} />
      <div className="max-w-3xl space-y-10">
        {content.body.map(([h, p]) => (
          <section key={h}>
            <h2 className="font-sans text-xl font-semibold text-ink">{h}</h2>
            <p className="mt-3 leading-relaxed text-ink-muted">{p}</p>
          </section>
        ))}
      </div>
    </PageShell>
  );
}

export function NotFound() {
  return (
    <main className="container-tpb py-28 text-center" data-testid="not-found-page">
      <Seo title="Page not found | The Perfect Break" description="This page has wandered off." path="/404" noindex />
      <p className="overline">404</p>
      <h1 className="mt-4 font-serif text-5xl font-medium">This page has gone on a break</h1>
      <p className="mx-auto mt-4 max-w-md text-ink-muted">The page you're after isn't here — but somewhere lovely is.</p>
      <Link to="/breaks" className="btn-primary mt-8" data-testid="not-found-cta">Find your perfect break <ArrowRight className="h-4 w-4" /></Link>
    </main>
  );
}
